import { $logger } from '@/boot/logger'
import { useCustomerReturnInterfaceDetection } from '@/composables/useCustomerReturnInterfaceDetection'

/**
 * @typedef {Object} ShippingCostDeduction
 * @property {boolean} shouldDeduct - Whether shipping costs should be deducted from the credit/refund amount
 * @property {number} shippingCost - The estimated shipping cost to deduct (0 when shouldDeduct is false)
 * @property {number} netAmount - The net credit/refund amount after any shipping deduction (sellingPrice - shippingCost)
 * @property {string} reason - Reason for the deduction decision (e.g. 'NON_PREFERENCE', 'PREFERENCE', etc.)
 */

/**
 * @typedef {Object} RestockingFeeDeduction
 * @property {boolean} shouldDeduct - Whether a restocking fee should be deducted (true for preference returns with fee > 0)
 * @property {number} feePercent - The restocking fee percentage configured for the retailer
 * @property {number} feeAmount - The calculated fee amount to deduct from the refund/credit
 */

/**
 * @typedef {Object} ReturnCreditPreview
 * @property {string} creditType - Type of credit the customer will receive: 'REFUND' | 'IN_STORE_CREDIT'
 * @property {string} creditTiming - When the credit will be issued: 'IMMEDIATE' | 'IN_TRANSIT' | 'AT_DELIVERY' | 'PENDING'
 * @property {number} estimatedShipCost - Estimated shipping cost for the return
 * @property {number} estimatedAmount - Estimated total credit/refund amount before any deductions
 * @property {number} sellingPrice - Original selling price of the item
 * @property {ShippingCostDeduction} shippingCostDeduction - Shipping cost deduction details
 * @property {RestockingFeeDeduction} [restockingFeeDeduction] - Restocking fee deduction details (present for preference returns when retailer has a fee configured)
 */

/**
 * @typedef {Object} ConversationState
 * @property {boolean} complete - Whether AI determined return process is complete for an item
 * @property {Array<string>} aiConversationLogIds - Array of conversation log IDs for tracking conversation history
 * @property {string|null} conversationId - Unique conversation identifier
 * @property {string|null} conversationStage - Current stage in the conversation flow
 * @property {ReturnCreditPreview|null} returnCreditPreview - Credit/refund preview from gather-return-info (available when API provides pricing data)
 * @property {string|null} customerMessage - Message from LLM to the customer
 * @property {string|boolean|number|Object|Array|null} customerInput - Customer's reply to LLM (various types depending on interface)
 * @property {Array<string>} instructions - Instructions for photo uploads from AI
 * @property {Object|null} nextAction - Next action from LLM including customer interface
 * @property {string|null} nextQuestion - Next question from the LLM
 * @property {Object|null} returnSummary - Summary of the return as determined by AI
 * @property {boolean} success - Whether last AI interaction was successful
 */

/**
 * @typedef {Object} ItemConversationState
 * @property {ConversationState} conversation - Conversation data for this item
 */

/**
 * Customer Return Conversation Store
 *
 * Manages per-item AI conversation state and flow for the customer return process.
 * This store handles:
 * - Per-item conversation state tracking (keyed by lineItemSessionId)
 * - Conversation state from AI responses
 * - Customer interface detection and custom interface handling
 * - Customer input tracking
 * - Conversation lifecycle (start, update, complete)
 *
 * @public API:
 * - State: activeItemSessionId, conversationHistory, receivedWrongItemById
 * - Computed (active item accessors): aiConversationLogIds, complete, conversationId, conversationStage, customerMessage, customerInput, instructions, nextAction, nextQuestion, returnSummary, success
 * - Computed (derived): customerInterface, isCustomInterfaceActive, handlingUploadPhotos, hasActiveConversation, hasNextAction, hasNextQuestion, hasReturnSummary
 * - Actions: setActiveItem, initializeItemConversation, setConversationState, setCustomerInput, setCustomerMessage, setNextAction, setInstructions, markComplete, markIncomplete, setSuccess, startConversation, clearCustomerInput, reset, getConversationState, getConversationForItem, setReceivedWrongItem, clearReceivedWrongItem, pushSnapshot, popSnapshot, restoreFromSnapshot, hasHistory
 *
 * @store customerReturnConversation
 */

// ============================================================================
// PRIVATE HELPER FUNCTIONS
// ============================================================================

/**
 * Validate AI response object structure
 * @param {any} aiResponse - Response to validate
 * @param {import('@/boot/logger').Logger} logger - Logger instance
 * @returns {boolean} True if valid
 * @private
 */
function _validateAIResponse(aiResponse, logger) {
  if (!aiResponse || typeof aiResponse !== 'object') {
    logger.error('Invalid AI response', { aiResponse })
    return false
  }
  return true
}

/**
 * Validate customer input type
 * @param {any} input - Input to validate
 * @param {import('@/boot/logger').Logger} logger - Logger instance
 * @returns {boolean} True if valid
 * @private
 */
function _validateCustomerInput(input, logger) {
  const validTypes = ['string', 'boolean', 'number', 'object']
  if (input !== null && !validTypes.includes(typeof input)) {
    logger.warn('Invalid customer input type', { input, type: typeof input })
    return false
  }
  return true
}

/**
 * Validate instructions array
 * @param {any} instructions - Instructions to validate
 * @param {import('@/boot/logger').Logger} logger - Logger instance
 * @returns {boolean} True if valid
 * @private
 */
function _validateInstructions(instructions, logger) {
  if (!Array.isArray(instructions)) {
    logger.warn('Invalid instructions type', { type: typeof instructions })
    return false
  }
  return true
}

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Default conversation state structure
 * @type {ConversationState}
 * @private
 */
const DEFAULT_CONVERSATION_STATE = {
  aiConversationLogIds: [],
  complete: false,
  conversationId: null,
  conversationStage: null,
  customerMessage: null,
  customerInput: null,
  instructions: [],
  nextAction: null,
  nextQuestion: null,
  returnCreditPreview: null,
  returnSummary: null,
  success: true,
}

// ============================================================================
// STORE DEFINITION
// ============================================================================

export const useCustomerReturnConversationStore = defineStore('customerReturnConversation', () => {
  // Composables
  const { isCustomInterface, requiresPhotoUpload } = useCustomerReturnInterfaceDetection()

  // ============================================================================
  // STATE
  // ============================================================================

  /**
   * Per-item conversation state keyed by lineItemSessionId
   * @type {Ref<Record<string, ConversationState>>}
   * @private
   */
  const conversations = ref({})

  /**
   * Per-item conversation history stack for back navigation
   * Each entry is a snapshot of the conversation state at that stage
   * Keyed by lineItemSessionId, values are arrays of snapshots
   * @type {Ref<Record<string, Array<ConversationState>>>}
   * @private
   */
  const conversationHistory = ref({})

  /**
   * Track items where user indicated wrong product was received
   * Keyed by lineItemSessionId
   * @type {Ref<Record<string, boolean>>}
   * @public
   */
  const receivedWrongItemById = ref({})

  /**
   * Currently active item's lineItemSessionId
   * @type {Ref<string|null>}
   * @public
   */
  const activeItemSessionId = ref(null)

  /**
   * Get active conversation or default state
   * @returns {ConversationState}
   * @private
   */
  const _getActiveConversation = () => {
    if (!activeItemSessionId.value) {
      return { ...DEFAULT_CONVERSATION_STATE }
    }
    return conversations.value[activeItemSessionId.value] || { ...DEFAULT_CONVERSATION_STATE }
  }

  // ============================================================================
  // COMPUTED PROPERTIES (Backward Compatible - Expose Active Item's State)
  // ============================================================================

  /**
   * Array of conversation log IDs for the active conversation
   * @type {ComputedRef<Array<string>>}
   * @public
   */
  const aiConversationLogIds = computed(() => _getActiveConversation().aiConversationLogIds)

  /**
   * Whether AI has determined the return process for an item is complete
   * @type {ComputedRef<boolean>}
   * @public
   */
  const complete = computed(() => _getActiveConversation().complete)

  /**
   * Unique identifier for the current conversation
   * @type {ComputedRef<string|null>}
   * @public
   */
  const conversationId = computed(() => _getActiveConversation().conversationId)

  /**
   * Current stage in the conversation flow
   * @type {ComputedRef<string|null>}
   * @public
   */
  const conversationStage = computed(() => _getActiveConversation().conversationStage)

  /**
   * Message from the LLM to display to the customer
   * @type {ComputedRef<string|null>}
   * @public
   */
  const customerMessage = computed(() => _getActiveConversation().customerMessage)

  /**
   * Customer's input/reply to the LLM (excluding questions)
   * @type {ComputedRef<string|boolean|number|Object|Array|null>}
   * @public
   */
  const customerInput = computed({
    get: () => _getActiveConversation().customerInput,
    set: value => setCustomerInput(value),
  })

  /**
   * Instructions for photo uploads from the AI
   * @type {ComputedRef<Array<string>>}
   * @public
   */
  const instructions = computed(() => _getActiveConversation().instructions)

  /**
   * Next action from LLM including the customer interface configuration
   * @type {ComputedRef<Object|null>}
   * @public
   */
  const nextAction = computed(() => _getActiveConversation().nextAction)

  /**
   * Next question from the LLM to ask the customer
   * @type {ComputedRef<string|null>}
   * @public
   */
  const nextQuestion = computed(() => _getActiveConversation().nextQuestion)

  /**
   * Summary of the return as determined by the AI
   * @type {ComputedRef<Object|null>}
   * @public
   */
  const returnSummary = computed(() => _getActiveConversation().returnSummary)

  /**
   * Credit estimate from gather-return-info (creditType, amounts, shipping deduction)
   * @type {ComputedRef<Object|null>}
   * @public
   */
  const returnCreditPreview = computed(() => _getActiveConversation().returnCreditPreview)

  /**
   * Indicates if the last AI interaction was successful
   * @type {ComputedRef<boolean>}
   * @public
   */
  const success = computed(() => _getActiveConversation().success)

  // ============================================================================
  // COMPUTED PROPERTIES (Custom Interface Detection)
  // ============================================================================

  /**
   * Current customer interface type from next action
   * @returns {string|null}
   * @public
   */
  const customerInterface = computed(() => nextAction.value?.customerInterface || null)

  /**
   * Whether the current interface is a custom interface (not using standard LLM UI)
   * Custom interfaces: wrongItem, productStateAssessment, packagingAssessment, etc.
   *
   * PEG-854 review (P1/P2): a COMPLETED item is never an active custom interface.
   * When gather-return-info completes an item (e.g. after an accepted
   * discount-to-keep offer) without a replacement nextAction, setConversationState
   * now drops the stale custom nextAction at the source — so neither the completion
   * actions nor GatherReturnInfo's title/message block render from stale offer copy.
   * The !complete gate here is kept as defense-in-depth: even if a completed item
   * somehow still carries a custom nextAction, AppReturnActions still shows Next
   * Item / Submit instead of stranding the shopper on a blank step.
   * @returns {boolean}
   * @public
   */
  const isCustomInterfaceActive = computed(() => !complete.value && isCustomInterface(customerInterface.value))

  /**
   * Whether the current interface requires photo upload handling
   * @returns {boolean}
   * @public
   */
  const handlingUploadPhotos = computed(() => requiresPhotoUpload(customerInterface.value))

  /**
   * Whether conversation has been started
   * @returns {boolean}
   * @public
   */
  const hasActiveConversation = computed(() => conversationId.value !== null)

  /**
   * Whether there is a next action to take
   * @returns {boolean}
   * @public
   */
  const hasNextAction = computed(() => nextAction.value !== null)

  /**
   * Whether there is a pending question for the customer
   * @returns {boolean}
   * @public
   */
  const hasNextQuestion = computed(() => nextQuestion.value !== null)

  /**
   * Whether the conversation has a return summary
   * @returns {boolean}
   * @public
   */
  const hasReturnSummary = computed(() => returnSummary.value !== null)

  // ============================================================================
  // ACTIONS (PUBLIC API)
  // ============================================================================

  /**
   * Set the active item for conversation
   * @param {string} lineItemSessionId - Line item session ID
   * @public
   */
  function setActiveItem(lineItemSessionId) {
    if (!lineItemSessionId) {
      $logger.warn('Conversation: Cannot set active item without lineItemSessionId')
      return
    }

    activeItemSessionId.value = lineItemSessionId

    // Initialize if doesn't exist
    if (!conversations.value[lineItemSessionId]) {
      initializeItemConversation(lineItemSessionId)
    }
  }

  /**
   * Initialize conversation for a specific item
   * @param {string} lineItemSessionId - Line item session ID
   * @public
   */
  function initializeItemConversation(lineItemSessionId) {
    if (!lineItemSessionId) {
      $logger.warn('Conversation: Cannot initialize without lineItemSessionId')
      return
    }

    // Don't reinitialize if already exists
    if (conversations.value[lineItemSessionId]) {
      return
    }

    conversations.value[lineItemSessionId] = { ...DEFAULT_CONVERSATION_STATE }
  }

  /**
   * Update conversation state from AI response
   * @param {Object} aiResponse - Response from AI containing conversation state
   * @param {string[]} [aiResponse.aiConversationLogIds] - Conversation log IDs
   * @param {string} [aiResponse.conversationId] - Conversation ID
   * @param {string} [aiResponse.conversationStage] - Current stage
   * @param {string} [aiResponse.customerMessage] - Message to customer
   * @param {Array} [aiResponse.instructions] - Photo upload instructions
   * @param {Object} [aiResponse.nextAction] - Next action configuration
   * @param {string} [aiResponse.nextQuestion] - Next question
   * @param {Object} [aiResponse.returnSummary] - Return summary
   * @param {boolean} [aiResponse.success] - Success status
   * @param {boolean} [aiResponse.complete] - Whether process is complete
   * @public
   */
  function setConversationState(aiResponse) {
    if (!_validateAIResponse(aiResponse, $logger)) {
      return
    }

    if (!activeItemSessionId.value) {
      $logger.warn('Conversation: Cannot set state without active item')
      return
    }

    const activeConversation = conversations.value[activeItemSessionId.value]
    if (!activeConversation) {
      $logger.warn('Conversation: Active item conversation not initialized')
      return
    }

    // Update active item's conversation state from AI response
    if (aiResponse.conversationId !== undefined) {
      activeConversation.conversationId = aiResponse.conversationId
    }
    if (aiResponse.conversationStage !== undefined) {
      activeConversation.conversationStage = aiResponse.conversationStage
    }
    if (aiResponse.customerMessage !== undefined) {
      activeConversation.customerMessage = aiResponse.customerMessage
    }
    if (aiResponse.instructions !== undefined) {
      activeConversation.instructions = Array.isArray(aiResponse.instructions) ? aiResponse.instructions : []
    }
    if (aiResponse.nextAction !== undefined) {
      activeConversation.nextAction = aiResponse.nextAction
    } else if (aiResponse.complete === true && isCustomInterface(activeConversation.nextAction?.customerInterface)) {
      // PEG-854 review (P2): when a custom interface (e.g. an accepted
      // discount-to-keep offer) completes the item WITHOUT a replacement nextAction,
      // drop the stale custom nextAction at the source. GatherReturnInfo gates its
      // title/message block on isCustomInterfaceActive, so leaving the old offer
      // nextAction in place would render the offer's pageTitle/message above the
      // completion actions. Clearing it leaves customerInterface null so the
      // completed item renders its completion state cleanly. Only custom interfaces
      // are cleared, so standard (non-custom) completion responses are untouched.
      activeConversation.nextAction = null
    }
    if (aiResponse.nextQuestion !== undefined) {
      activeConversation.nextQuestion = aiResponse.nextQuestion
    }
    if (aiResponse.returnCreditPreview !== undefined) {
      activeConversation.returnCreditPreview = aiResponse.returnCreditPreview
    }
    if (aiResponse.returnSummary !== undefined) {
      activeConversation.returnSummary = aiResponse.returnSummary
    }
    if (aiResponse.success !== undefined) {
      activeConversation.success = aiResponse.success
    }
    if (aiResponse.complete !== undefined) {
      activeConversation.complete = aiResponse.complete
    }
    if (aiResponse.aiConversationLogIds !== undefined) {
      activeConversation.aiConversationLogIds = aiResponse.aiConversationLogIds
    }
  }

  /**
   * Set customer's input response
   * @param {string|boolean|number|Object|Array|null} input - Customer's input (can be text, boolean, object, etc.)
   * @public
   */
  function setCustomerInput(input) {
    if (!_validateCustomerInput(input, $logger)) {
      return
    }

    if (!activeItemSessionId.value) {
      $logger.warn('Conversation: Cannot set customer input without active item')
      return
    }

    const activeConversation = conversations.value[activeItemSessionId.value]
    if (!activeConversation) {
      $logger.warn('Conversation: Active item conversation not initialized')
      return
    }

    // Spread array inputs to ensure a new array reference, guaranteeing Vue
    // reactivity triggers for downstream computeds (e.g. photo upload checks)
    activeConversation.customerInput = Array.isArray(input) ? [...input] : input
  }

  /**
   * Set the customer message
   * @param {string} message - Message to display to customer
   * @public
   */
  function setCustomerMessage(message) {
    if (!activeItemSessionId.value) {
      $logger.warn('Conversation: Cannot set customer message without active item')
      return
    }

    const activeConversation = conversations.value[activeItemSessionId.value]
    if (!activeConversation) {
      $logger.warn('Conversation: Active item conversation not initialized')
      return
    }

    activeConversation.customerMessage = message
  }

  /**
   * Set the next action
   * @param {Object} action - Next action configuration
   * @public
   */
  function setNextAction(action) {
    if (!activeItemSessionId.value) {
      $logger.warn('Conversation: Cannot set next action without active item')
      return
    }

    const activeConversation = conversations.value[activeItemSessionId.value]
    if (!activeConversation) {
      $logger.warn('Conversation: Active item conversation not initialized')
      return
    }

    activeConversation.nextAction = action
  }

  /**
   * Set instructions for photo upload
   * @param {Array<string>} newInstructions - Array of instruction strings
   * @public
   */
  function setInstructions(newInstructions) {
    if (!_validateInstructions(newInstructions, $logger)) {
      return
    }

    if (!activeItemSessionId.value) {
      $logger.warn('Conversation: Cannot set instructions without active item')
      return
    }

    const activeConversation = conversations.value[activeItemSessionId.value]
    if (!activeConversation) {
      $logger.warn('Conversation: Active item conversation not initialized')
      return
    }

    activeConversation.instructions = newInstructions
  }

  /**
   * Mark the conversation as complete
   * @public
   */
  function markComplete() {
    if (!activeItemSessionId.value) {
      $logger.warn('Conversation: Cannot mark complete without active item')
      return
    }

    const activeConversation = conversations.value[activeItemSessionId.value]
    if (!activeConversation) {
      $logger.warn('Conversation: Active item conversation not initialized')
      return
    }

    activeConversation.complete = true
  }

  /**
   * Mark conversation as incomplete (e.g., when starting new item)
   * @public
   */
  function markIncomplete() {
    if (!activeItemSessionId.value) {
      $logger.warn('Conversation: Cannot mark incomplete without active item')
      return
    }

    const activeConversation = conversations.value[activeItemSessionId.value]
    if (!activeConversation) {
      $logger.warn('Conversation: Active item conversation not initialized')
      return
    }

    activeConversation.complete = false
  }

  /**
   * Set success status of last AI interaction
   * @param {boolean} status - Success status
   * @public
   */
  function setSuccess(status) {
    if (!activeItemSessionId.value) {
      $logger.warn('Conversation: Cannot set success without active item')
      return
    }

    const activeConversation = conversations.value[activeItemSessionId.value]
    if (!activeConversation) {
      $logger.warn('Conversation: Active item conversation not initialized')
      return
    }

    activeConversation.success = status
  }

  /**
   * Start a new conversation (deprecated in favor of setActiveItem)
   * @param {string} conversationIdValue - Initial conversation ID
   * @public
   * @deprecated Use setActiveItem instead - kept for backward compatibility
   */
  function startConversation(conversationIdValue) {
    if (!activeItemSessionId.value) {
      $logger.warn('Conversation: Cannot start conversation without active item')
      return
    }

    const activeConversation = conversations.value[activeItemSessionId.value]
    if (!activeConversation) {
      $logger.warn('Conversation: Active item conversation not initialized')
      return
    }

    activeConversation.conversationId = conversationIdValue
    activeConversation.complete = false
    activeConversation.success = true
  }

  /**
   * Clear customer input (e.g., after submission)
   * @public
   */
  function clearCustomerInput() {
    if (!activeItemSessionId.value) {
      $logger.warn('Conversation: Cannot clear customer input without active item')
      return
    }

    const activeConversation = conversations.value[activeItemSessionId.value]
    if (!activeConversation) {
      $logger.warn('Conversation: Active item conversation not initialized')
      return
    }

    activeConversation.customerInput = null
  }

  /**
   * Reset conversation state
   * @param {Object} options - Reset options
   * @param {boolean} options.keepConversationId - Whether to keep conversation ID (deprecated)
   * @param {string} [options.lineItemSessionId] - Optional: Reset specific item, or all if omitted
   * @public
   */
  function reset({ keepConversationId: _keepConversationId = false, lineItemSessionId } = {}) {
    if (lineItemSessionId) {
      // Reset specific item
      if (conversations.value[lineItemSessionId]) {
        conversations.value[lineItemSessionId] = { ...DEFAULT_CONVERSATION_STATE }
      }
    } else {
      // Reset all conversations
      conversations.value = {}
      activeItemSessionId.value = null
    }
  }

  /**
   * Get full conversation state as an object for the active item
   * @returns {ConversationState}
   * @public
   */
  function getConversationState() {
    return _getActiveConversation()
  }

  /**
   * Get conversation state for a specific item
   * @param {string} lineItemSessionId - Line item session ID
   * @returns {ConversationState|null}
   * @public
   */
  function getConversationForItem(lineItemSessionId) {
    if (!lineItemSessionId) return null
    return conversations.value[lineItemSessionId] || null
  }

  /**
   * Mark that user indicated they received the wrong item
   * Called when conversation stage moves to wrongItem
   * @param {string} lineItemSessionId - Line item session ID
   * @public
   */
  function setReceivedWrongItem(lineItemSessionId) {
    if (!lineItemSessionId) {
      $logger.warn('Conversation: Cannot set received wrong item without lineItemSessionId')
      return
    }

    receivedWrongItemById.value[lineItemSessionId] = true
  }

  /**
   * Clear the wrong item flag for a specific item
   * Called when customer confirms they received the correct item during product verification,
   * ensuring stale wrong item tracking doesn't persist.
   * @param {string} lineItemSessionId - Line item session ID
   * @public
   */
  function clearReceivedWrongItem(lineItemSessionId) {
    if (!lineItemSessionId) {
      $logger.warn('Conversation: Cannot clear received wrong item without lineItemSessionId')
      return
    }

    delete receivedWrongItemById.value[lineItemSessionId]
  }

  // ============================================================================
  // CONVERSATION HISTORY (for back navigation)
  // ============================================================================

  /**
   * Push current conversation state to history stack before transitioning to next stage
   * @public
   */
  function pushSnapshot() {
    if (!activeItemSessionId.value) {
      $logger.warn('Conversation: Cannot push snapshot without active item')
      return
    }

    const activeConversation = conversations.value[activeItemSessionId.value]
    if (!activeConversation) {
      $logger.warn('Conversation: Active item conversation not initialized', {
        lineItemSessionId: activeItemSessionId.value,
      })
      return
    }

    // Initialize history array if it doesn't exist
    if (!conversationHistory.value[activeItemSessionId.value]) {
      conversationHistory.value[activeItemSessionId.value] = []
    }

    // Create a deep copy of the current conversation state
    // Note: customerInterface is a store-level computed derived from nextAction.customerInterface,
    // so it doesn't exist as a property on the raw conversation object. We capture the full
    // nextAction object (which contains customerInterface) via JSON deep clone.
    // We use JSON.parse/stringify instead of structuredClone because Vue reactive proxies
    // (especially nested arrays/objects in customerInput) can contain non-cloneable internals.
    const snapshot = {
      aiConversationLogIds: activeConversation.aiConversationLogIds ? [...activeConversation.aiConversationLogIds] : [],
      conversationStage: activeConversation.conversationStage,
      customerMessage: activeConversation.customerMessage,
      nextQuestion: activeConversation.nextQuestion,
      nextAction: activeConversation.nextAction ? JSON.parse(JSON.stringify(activeConversation.nextAction)) : null,
      customerInput:
        activeConversation.customerInput != null ? JSON.parse(JSON.stringify(activeConversation.customerInput)) : null,
      returnCreditPreview: activeConversation.returnCreditPreview
        ? JSON.parse(JSON.stringify(activeConversation.returnCreditPreview))
        : null,
      returnSummary: activeConversation.returnSummary ? JSON.parse(JSON.stringify(activeConversation.returnSummary)) : null,
      instructions: activeConversation.instructions?.length ? JSON.parse(JSON.stringify(activeConversation.instructions)) : [],
    }

    const history = conversationHistory.value[activeItemSessionId.value]
    const snapshotLogId = snapshot.aiConversationLogIds?.[0] || null

    if (snapshotLogId) {
      const existingIndex = history.findIndex(
        s => Array.isArray(s.aiConversationLogIds) && s.aiConversationLogIds[0] === snapshotLogId
      )

      if (existingIndex !== -1) {
        history[existingIndex] = snapshot
        return
      }
    }

    history.push(snapshot)
  }

  /**
   * Pop the last snapshot from history stack
   * @returns {ConversationState|null} The previous snapshot, or null if history is empty
   * @public
   */
  function popSnapshot() {
    if (!activeItemSessionId.value) {
      $logger.warn('Conversation: Cannot pop snapshot without active item')
      return null
    }

    const history = conversationHistory.value[activeItemSessionId.value]
    if (!history || history.length === 0) {
      $logger.warn('Conversation: No history to pop')
      return null
    }

    const snapshot = history.pop()

    return snapshot
  }

  /**
   * Restore conversation state from a snapshot
   * @param {ConversationState} snapshot - The snapshot to restore
   * @public
   */
  function restoreFromSnapshot(snapshot) {
    if (!snapshot) {
      $logger.warn('Conversation: Cannot restore from null snapshot')
      return
    }

    if (!activeItemSessionId.value) {
      $logger.warn('Conversation: Cannot restore snapshot without active item')
      return
    }

    const activeConversation = conversations.value[activeItemSessionId.value]
    if (!activeConversation) {
      $logger.warn('Conversation: Active item conversation not initialized')
      return
    }

    // Replace the entire conversation object to guarantee Vue reactivity triggers.
    // Mutating individual properties on a deeply-nested reactive object can sometimes
    // fail to trigger computed re-evaluation when multiple properties change at once.
    // By replacing the whole object, Vue sees a single top-level property change on
    // the conversations ref and all dependent computeds re-evaluate.
    conversations.value[activeItemSessionId.value] = {
      ...activeConversation,
      aiConversationLogIds: snapshot.aiConversationLogIds,
      conversationStage: snapshot.conversationStage,
      customerMessage: snapshot.customerMessage,
      nextQuestion: snapshot.nextQuestion,
      nextAction: snapshot.nextAction,
      customerInput: snapshot.customerInput,
      returnCreditPreview: snapshot.returnCreditPreview || null,
      returnSummary: snapshot.returnSummary,
      instructions: snapshot.instructions || [],
    }
  }

  /**
   * Check if there is history available for back navigation
   * @returns {boolean} True if history exists for the active item
   * @public
   */
  function hasHistory() {
    if (!activeItemSessionId.value) return false
    const history = conversationHistory.value[activeItemSessionId.value]
    return history && history.length > 0
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  return {
    // Computed - active item conversation accessors
    complete,
    conversationId,
    conversationStage,
    conversationHistory, // State (ref)
    customerMessage,
    customerInput,
    instructions,
    nextAction,
    nextQuestion,
    returnCreditPreview,
    returnSummary,
    success,
    activeItemSessionId, // State (ref)
    receivedWrongItemById, // State (ref)
    // Computed - derived properties
    aiConversationLogIds,
    customerInterface,
    isCustomInterfaceActive,
    handlingUploadPhotos,
    hasActiveConversation,
    hasNextAction,
    hasNextQuestion,
    hasReturnSummary,
    // Actions
    setActiveItem,
    initializeItemConversation,
    setConversationState,
    setCustomerInput,
    setCustomerMessage,
    setNextAction,
    setInstructions,
    markComplete,
    markIncomplete,
    setSuccess,
    startConversation,
    clearCustomerInput,
    reset,
    getConversationState,
    getConversationForItem,
    clearReceivedWrongItem,
    setReceivedWrongItem,
    // History actions
    pushSnapshot,
    popSnapshot,
    restoreFromSnapshot,
    hasHistory,
  }
})
